import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HttpService from "../services/HttpService";

const initialFeedback = {
  open: false,
  severity: "success",
  message: "",
};

const isStringMap = (value) =>
  value &&
  !Array.isArray(value) &&
  typeof value === "object" &&
  Object.values(value).every((entry) => typeof entry === "string");

export default function GuardLockerManager() {
  const [lockerId, setLockerId] = useState("");
  const [token, setToken] = useState("");
  const [bagContent, setBagContent] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(initialFeedback);

  const showError = (error) => {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "The GuardLocker request failed";
    setFeedback({ open: true, severity: "error", message });
  };

  const fetchData = async (showSuccess = true) => {
    if (!lockerId.trim() || !token.trim()) {
      setFeedback({
        open: true,
        severity: "error",
        message: "Locker ID and token are required",
      });
      return;
    }

    setIsFetching(true);
    try {
      const response = await HttpService.getAxiosClient().post(
        `${window.API_URL}/guardlocker/sst/fetch`,
        { lockerId: lockerId.trim(), token: token.trim() }
      );
      setBagContent(JSON.stringify(response.data, null, 2));
      if (showSuccess) {
        setFeedback({
          open: true,
          severity: "success",
          message: "Bag content loaded",
        });
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const saveData = async (event) => {
    event.preventDefault();

    let parsedContent;
    try {
      parsedContent = JSON.parse(bagContent);
    } catch {
      setFeedback({
        open: true,
        severity: "error",
        message: "Bag content must be valid JSON",
      });
      return;
    }

    if (!isStringMap(parsedContent)) {
      setFeedback({
        open: true,
        severity: "error",
        message: "Bag content must be a JSON object with string values",
      });
      return;
    }

    setIsSaving(true);
    try {
      await HttpService.getAxiosClient().put(`${window.API_URL}/guardlocker/sst`, {
        lockerId: lockerId.trim(),
        token: token.trim(),
        bagContent: parsedContent,
      });
      setFeedback({
        open: true,
        severity: "success",
        message: "Bag content merged successfully",
      });
      await fetchData(false);
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", width: "100%" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        GuardLocker Manager
      </Typography>
      <Paper component="form" onSubmit={saveData} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="Locker ID"
            placeholder="SST:onboarding:user@example.com"
            value={lockerId}
            onChange={(event) => setLockerId(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Token"
            type="password"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Bag content"
            helperText="Submit only the fields to merge. Every value must be a string."
            value={bagContent}
            onChange={(event) => setBagContent(event.target.value)}
            multiline
            minRows={16}
            fullWidth
            inputProps={{ spellCheck: false }}
          />
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="outlined"
              onClick={fetchData}
              disabled={isFetching || isSaving}
            >
              {isFetching ? <CircularProgress size={22} /> : "Load data"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFetching || isSaving}
            >
              {isSaving ? <CircularProgress size={22} /> : "Merge changes"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback((current) => ({ ...current, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
